import type { components } from "@contracts/generated/editor-service";

import { newDraftId, splitPartKey, type DraftPart } from "./assetContent";
import { joinPartKey } from "./namedParts";

/**
 * A Summary prompt's ordered example pairs, projected the way the named prompt
 * page edits them (#916).
 *
 * WHAT AN EXAMPLE SET IS, IN ONE SENTENCE THAT DECIDES EVERYTHING BELOW: it is
 * ONE structured part of an immutable version — `<language>/examples`, holding
 * `{items: [{input, output}]}` — which the pinned summary agent reads back as
 * `examples/example_N/{input,output}.txt` and prepends to the real request as
 * user/assistant turns. So `items[i]` IS `example_{i+1}`, the array order IS the
 * runtime's reading order, and the panel that renders it is rendering the
 * generation context rather than a list of files.
 *
 * ## THE ONE RULE THE PINNED LOADER IMPOSES, AND WHY IT IS RESTATED HERE
 *
 * `FilesystemConfigProvider.list_clinical_dir` returns the UNION of the base
 * pack's and the overlay's example directories, and there is no tombstone: an
 * authored run of N pairs emits `example_1 … example_N` and every BASE index
 * above N survives into the runtime set. `summary_examples.validate_summary_examples`
 * is the authority and refuses a set shorter than the highest inherited index,
 * with nothing written.
 *
 * This module states the SAME arithmetic — `items.length >= inheritedCount` —
 * and that is deliberately not a second opinion. The ticket requires the
 * refusal to land BEFORE the mutation ("unchanged persisted/effective state"),
 * and a refusal that arrives as a 422 after the draft already dropped the pair
 * has changed the state it was supposed to leave alone. So the check runs where
 * the press happens, and the service's identical refusal remains the authority
 * behind it. Nothing here is stricter than the server: a length the service
 * accepts is a length this accepts.
 *
 * ## WHAT IT DELIBERATELY DOES NOT KNOW
 *
 * Which pairs BASE ships. That is `GET /library/assets/{id}/summary-examples`'s
 * answer — the service reads the mounted pack and marks each effective pair
 * `inherited` — and the browser has no second route to it. So provenance here is
 * a projection of the service's own answer plus the clinician's in-flight edits,
 * exactly as `rulesContent` composes the substitution projection: the one part
 * of the picture no server can hold is the unsaved draft in this tab.
 */

export type SummaryExampleSetRead =
  components["schemas"]["SummaryExampleSetRead"];
export type SummaryExampleRead = components["schemas"]["SummaryExample"];

/** One stored pair, as the `examples` document holds it. */
export interface SummaryExamplePair {
  /** The example user turn. Never empty — the pinned loader refuses that. */
  input: string;
  /** The example assistant turn. `""` is a real, valid teaching example. */
  output: string;
}

/** The `examples` document of one language, as the part's JSON text holds it. */
export interface SummaryExampleDocument {
  items: SummaryExamplePair[];
}

/**
 * Where one row on screen came from.
 *
 * THREE STATES BECAUSE THE PINNED LOADER EXPRESSES EXACTLY THREE EDITS, and
 * naming them after the edits is what keeps the panel from implying a fourth:
 *
 *   * `inherited` — this position is one base occupies, and the pair still
 *     carries base's own bytes. Untouched inheritance.
 *   * `replaced` — this position is one base occupies and the pair does not
 *     carry base's bytes. The overlay's file wins at that index; base's is not
 *     deleted, it is written over.
 *   * `added` — this position is beyond every base index. An authored-only
 *     addition, and the only kind of row whose removal is always expressible.
 */
export type SummaryExampleOrigin = "inherited" | "replaced" | "added";

/** One pair as the panel renders it. */
export interface SummaryExampleRow extends SummaryExamplePair {
  /** 1-based position, which is the `example_N` directory it becomes. */
  index: number;
  origin: SummaryExampleOrigin;
}

/**
 * What the panel needs to render and edit one language's set.
 *
 * `authored` is the SET-level fact — whether the draft carries an `examples`
 * part at all — and it is what separates "reset" from "remove a pair": reset
 * removes the part and restores whole-set inheritance, and it is offered only
 * when there is a part to remove.
 */
export interface SummaryExampleView {
  language: string;
  authored: boolean;
  /**
   * The highest example index the pinned base pack occupies at this target.
   *
   * The floor an authored set may not go below, straight off the service. `0`
   * when base ships none — and also when the service could not read the base
   * pack at all, which `baseAvailable` is what distinguishes.
   */
  inheritedCount: number;
  /** False when this deployment has no readable base pack mounted. */
  baseAvailable: boolean;
  rows: SummaryExampleRow[];
}

/** The `examples` part for one language, or `null`. */
export function findExamplesPart(
  parts: readonly DraftPart[],
  language: string,
): DraftPart | null {
  return (
    parts.find((part) => {
      const [partLanguage, role] = splitPartKey(part.key);
      return role === EXAMPLES_ROLE && partLanguage === language;
    }) ?? null
  );
}

/** The service's own role name for the document, never spelled twice. */
const EXAMPLES_ROLE = "examples";

/**
 * The pairs a draft part holds, or `null` when its text is not a set.
 *
 * TOLERANT, on `summary_examples.stored_example_set`'s rule: a version row is
 * immutable, so a shape this cannot read must render as "not a set this editor
 * can author" rather than throw on somebody's read path. The write gate is what
 * keeps unreadable sets from being stored; this is the reader that has to
 * survive one anyway.
 *
 * Whitespace is NOT trimmed anywhere here. A pair is emitted verbatim into
 * `input.txt`/`output.txt`, so a trailing newline is content — the source packs'
 * own files end in one, and a set that silently lost it would export bytes that
 * differ from the pack it is supposed to equal.
 */
export function readExampleDocument(
  part: DraftPart | null,
): SummaryExampleDocument | null {
  if (part === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(part.text);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) {
    return null;
  }
  const items = (parsed as { items?: unknown }).items;
  if (!Array.isArray(items)) {
    return null;
  }
  const pairs: SummaryExamplePair[] = [];
  for (const item of items) {
    if (typeof item !== "object" || item === null) {
      return null;
    }
    const { input, output } = item as { input?: unknown; output?: unknown };
    // BOTH fields required, and `output` required SEPARATELY from being
    // non-empty. `""` is base canon's own `patient_additions` output and a
    // valid teaching example; an ABSENT output is what the loader raises on.
    // Reading a document that omits it as "no set" rather than as an empty
    // string is what keeps this editor from quietly repairing an invalid
    // stored version into a different valid one.
    if (typeof input !== "string" || typeof output !== "string") {
      return null;
    }
    pairs.push({ input, output });
  }
  return { items: pairs };
}

/** The document written back into a draft part, in the panel's own order. */
export function writeExampleDocument(
  parts: readonly DraftPart[],
  language: string,
  items: readonly SummaryExamplePair[],
): DraftPart[] {
  // Serialised the way `toDraft` serialises every other structured part —
  // pretty-printed, because this is the buffer the rest of the editor reads and
  // `toPartsContent` re-parses it into the canonical form the server hashes.
  const text = JSON.stringify({ items }, null, 2);
  const existing = findExamplesPart(parts, language);
  if (existing !== null) {
    return parts.map((part) =>
      part.id === existing.id ? { ...part, text } : part,
    );
  }
  return [
    ...parts,
    {
      id: newDraftId(),
      key: joinPartKey(language, EXAMPLES_ROLE),
      kind: "structured" as const,
      text,
    },
  ];
}

/**
 * The `examples` part removed — RESET, and the only supported way to stop
 * authoring a set.
 *
 * Distinct from removing a pair, and the distinction is the ticket's: a pair
 * leaves the authored run, while this leaves the version with no run at all, so
 * the target inherits base's set again. It is never reachable by emptying an
 * output, which is a different valid state, and never by writing an empty
 * `items` array, which the service refuses (`min_length=1`).
 */
export function removeExampleDocument(
  parts: readonly DraftPart[],
  language: string,
): DraftPart[] {
  const existing = findExamplesPart(parts, language);
  return existing === null
    ? [...parts]
    : parts.filter((part) => part.id !== existing.id);
}

/** Where the `examples` part of one language sits in the parts array, or -1. */
export function examplesPartIndex(
  parts: readonly DraftPart[],
  language: string,
): number {
  const existing = findExamplesPart(parts, language);
  return existing === null
    ? -1
    : parts.findIndex((part) => part.id === existing.id);
}

/**
 * One removed part put back exactly as it was — the undo half of a reset.
 *
 * VERBATIM AND IN PLACE, which is what separates an undo from a rewrite.
 * Re-deriving the document from the rendered pairs would restore the right
 * PAIRS and still change the draft in two ways a clinician never asked for:
 * `parts` is an ordered list by contract, so appending puts the set after the
 * prompt when it was stored before it, and re-serialising drops anything the
 * reader does not model. Both change the content this draft would publish, and
 * `isSameDraft` compares position by position — so an inexact undo leaves the
 * draft dirty and Save offering to publish a version nobody edited.
 *
 * The index is where the part sat when it was taken out; a draft that has since
 * grown or shrunk elsewhere clamps rather than throwing, because putting the
 * part back at the end is still better than losing it.
 */
export function restoreExamplePart(
  parts: readonly DraftPart[],
  part: DraftPart,
  index: number,
): DraftPart[] {
  const next = [...parts];
  next.splice(Math.max(0, Math.min(index, next.length)), 0, part);
  return next;
}

/**
 * The languages a Summary prompt's examples may be authored at.
 *
 * THE ASSET'S OWN, never a control that invents a prefix (2026-08-25
 * amendment). An `examples` part must be language-prefixed — the pack path is
 * `summary_agent/{lang}/per_key/…`, so `validate_summary_examples` refuses a
 * neutral key at the key the client sent — which makes a prompt stored under a
 * bare key a real state with no authorable set, stated rather than offered.
 *
 * Ordered by first appearance in the parts array, so the panel's groups follow
 * the stored order exactly as `namedRows` does.
 */
export function exampleLanguages(parts: readonly DraftPart[]): string[] {
  const seen: string[] = [];
  for (const part of parts) {
    const [language] = splitPartKey(part.key);
    if (language !== null && !seen.includes(language)) {
      seen.push(language);
    }
  }
  return seen;
}

/**
 * The pairs base canon ships, as far as the SERVICE's readback reveals them.
 *
 * Keyed by index, and populated only from pairs the service marked `inherited` —
 * which is exactly the set of positions whose bytes are known to be base's. At a
 * position this version already replaced, base's own bytes are not published by
 * any route, and that is fine: such a row is `replaced` however it is edited,
 * so the missing entry never changes an answer.
 */
function baseByIndex(
  read: SummaryExampleSetRead | undefined,
): Map<number, SummaryExamplePair> {
  const base = new Map<number, SummaryExamplePair>();
  for (const example of read?.examples ?? []) {
    if (example.provenance.inherited) {
      base.set(example.index, { input: example.input, output: example.output });
    }
  }
  return base;
}

/**
 * One language's set as the panel renders it: the DRAFT's pairs, with the
 * SERVICE's provenance.
 *
 * THE DRAFT IS THE CONTENT AND THE READBACK IS THE PROVENANCE, and mixing that
 * up either way is a bug the ticket names. Rendering the readback's pairs would
 * discard unsaved work on every background refetch — "preserve unsaved pairs
 * across validation errors, stale saves and prompt edits" is exactly that
 * property. Deriving provenance from the draft would be the browser deciding
 * what base ships, which it cannot read.
 *
 * With no authored part the two coincide: the effective set IS base's, so the
 * rows are the readback's own and every one of them is inherited.
 */
export function exampleView(
  parts: readonly DraftPart[],
  language: string,
  read: SummaryExampleSetRead | undefined,
): SummaryExampleView {
  const authored = readExampleDocument(findExamplesPart(parts, language));
  const inheritedCount = read?.inheritedCount ?? 0;
  const base = baseByIndex(read);
  const items =
    authored?.items ??
    (read?.examples ?? []).map((example) => ({
      input: example.input,
      output: example.output,
    }));
  return {
    language,
    authored: authored !== null,
    inheritedCount,
    baseAvailable: read?.baseAvailable ?? false,
    rows: items.map((pair, position) => {
      const index = position + 1;
      const inherited = base.get(index);
      return {
        index,
        input: pair.input,
        output: pair.output,
        origin:
          index > inheritedCount
            ? "added"
            : inherited !== undefined &&
                inherited.input === pair.input &&
                inherited.output === pair.output
              ? "inherited"
              : "replaced",
      };
    }),
  };
}

/**
 * The set a clinician's FIRST edit authors, when the version has none.
 *
 * Seeded from the effective set rather than from nothing, and that is forced by
 * the union rather than chosen for convenience: an authored run of one new pair
 * at a target that inherits three would leave base's second and third in the
 * runtime set, which the service refuses and the ticket forbids simulating.
 * Carrying the inherited pairs through verbatim is what makes the new pair an
 * ADDITION rather than a truncation, and each carried pair reads back as
 * `inherited` provenance because it is byte-identical.
 */
export function seedAuthoredItems(
  view: SummaryExampleView,
): SummaryExamplePair[] {
  return view.rows.map((row) => ({ input: row.input, output: row.output }));
}

/** One pair's half replaced, everything else untouched. */
export function updateExamplePair(
  items: readonly SummaryExamplePair[],
  index: number,
  half: "input" | "output",
  text: string,
): SummaryExamplePair[] {
  return items.map((pair, position) =>
    position + 1 === index ? { ...pair, [half]: text } : pair,
  );
}

/**
 * A new empty pair, appended.
 *
 * APPENDED rather than inserted at a chosen position: the set is positional and
 * the position that is unambiguously free is the one after the last, so a new
 * pair never displaces an inherited one by accident. Reordering afterwards is
 * what puts it where the clinician wants it, and that is a separate, visible,
 * announced action.
 */
export function addExamplePair(
  items: readonly SummaryExamplePair[],
): SummaryExamplePair[] {
  return [...items, { input: "", output: "" }];
}

/** One pair removed. `refuseRemoval` decides whether this may be called. */
export function removeExamplePair(
  items: readonly SummaryExamplePair[],
  index: number,
): SummaryExamplePair[] {
  return items.filter((_pair, position) => position + 1 !== index);
}

/** The pair at `index` moved to `to`, both 1-based. */
export function moveExamplePair(
  items: readonly SummaryExamplePair[],
  index: number,
  to: number,
): SummaryExamplePair[] {
  if (index === to || to < 1 || to > items.length) {
    return [...items];
  }
  const next = [...items];
  const [moved] = next.splice(index - 1, 1);
  next.splice(to - 1, 0, moved as SummaryExamplePair);
  return next;
}

/**
 * Why THIS ROW may not be removed, or `null`.
 *
 * THE REFUSAL THE TICKET REQUIRES BEFORE THE MUTATION, and it is about the row
 * rather than only about the count. Three arms, in this order because they are
 * different states and the most specific one is the one a clinician pressed:
 *
 *   * **the row IS an inherited base pair.** Removing it drops base's own bytes
 *     out of the effective set — the later pairs shift up and the position is
 *     covered by somebody else's — which is precisely the "omit an inherited
 *     base example" the S7 contract §6 says must be rejected. The permitted
 *     removal is of an authored-only ADDITION.
 *   * base occupies indices this run would stop covering — the length floor,
 *     which catches removing a REPLACED row that would shorten the run below
 *     what the union leaves behind;
 *   * the set would become empty while it is still authored, which
 *     `SummaryExampleSet.items` refuses (`min_length=1`) and which reset is the
 *     supported spelling of.
 *
 * WHY THE FIRST ARM IS THE CLIENT'S AND NOT THE SERVER'S, since this module
 * otherwise never refuses what the service accepts. `validate_summary_examples`
 * sees one submitted SET and cannot see the operation that produced it: the set
 * a removal leaves behind is byte-identical to the set an allowed REPLACEMENT
 * of the same position leaves behind, so no server-side rule can separate them.
 * The operation is only knowable where the press happened. This is therefore
 * the ticket's product refusal rather than a second opinion on a server gate,
 * and it never accepts anything the service refuses — the other two arms are
 * the service's own comparison, applied early so nothing is written.
 *
 * A row the clinician has ALREADY EDITED reads as `replaced` and may be
 * removed: at that point the pair on screen is their content, not the base
 * library's, and refusing to remove somebody's own text because of what used to
 * be at that position would be a rule about history rather than about the
 * runtime. The floor still applies to it.
 *
 * Every arm names the control that DOES do what the clinician is reaching for,
 * which is what makes them actionable rather than merely correct.
 */
export function refuseRemoval(
  view: SummaryExampleView,
  row: SummaryExampleRow,
): string | null {
  if (row.origin === "inherited") {
    return `Example ${row.index} is the base clinical library's own pair, and the pinned runtime has no way to remove one: an authored set replaces the inherited pairs position by position, so removing this row would leave the next pair standing in its place rather than deleting it. Change its text to replace it, or use “Reset to the base library” to inherit the whole set again.`;
  }
  const remaining = view.rows.length - 1;
  if (remaining < view.inheritedCount) {
    return `This Summary target inherits ${view.inheritedCount} example ${
      view.inheritedCount === 1 ? "pair" : "pairs"
    } from the base clinical library, and the pinned runtime has no way to remove one: an authored set replaces the inherited pairs position by position, so it has to keep at least ${view.inheritedCount}. Change this pair's text instead, or use “Reset to the base library” to inherit the whole set again.`;
  }
  if (remaining < 1) {
    return "An authored example set cannot be empty. Use “Reset to the base library” to stop authoring examples at this target — leaving a pair blank is a different thing, and an empty expected output is a valid example rather than a deletion.";
  }
  return null;
}

/**
 * Why this SET cannot be published, or `null`.
 *
 * The same length rule `refuseRemoval` enforces at the press, applied to a set
 * that is ALREADY short — which is a state no control here can produce and a
 * stored version can arrive in. A library version is immutable and long-lived,
 * so a set authored when base shipped two pairs is short the day base ships
 * three, and the union then appends base's third to whatever the project runs.
 * `validate_summary_examples` refuses that write; this says so before the Save,
 * addressed at the set rather than at a row, because no single pair is wrong.
 *
 * Silent while `baseAvailable` is false or the readback has not landed: the
 * floor is then unknown, and manufacturing a refusal from an unread authority
 * is the failure `PartialDataNotice` exists to avoid.
 */
export function refuseShortSet(view: SummaryExampleView): string | null {
  if (!view.authored || !view.baseAvailable) {
    return null;
  }
  if (view.rows.length >= view.inheritedCount) {
    return null;
  }
  return `This Summary target inherits ${view.inheritedCount} example pairs from the base clinical library and this set carries ${view.rows.length}. The pinned runtime unions the two, so the inherited pairs past position ${view.rows.length} would still reach the summary — add pairs until the set covers all ${view.inheritedCount} positions, or reset it to inherit the whole set again.`;
}

/**
 * Every authored pair this DRAFT cannot be published with, addressed at its own
 * position.
 *
 * ONE RULE, AND IT IS THE ENGINE'S. `load_per_key_few_shot_examples` refuses a
 * missing OR empty `input.txt` — `if not input_text: raise` — so an empty input
 * is a pair that breaks the summary at runtime, and `ExampleInput` carries
 * `min_length=1` for it. Catching it here is `assetContent`'s own policy for a
 * shape rule the wire model already states: a better message, attributable to
 * the row a clinician is looking at, not a second opinion.
 *
 * THE OUTPUT HAS NO SUCH RULE AND MUST NEVER GROW ONE HERE. `""` is base
 * canon's own `patient_additions/example_1/output.txt` and the state both
 * source packs replace, so an editor that treated an empty output as unfinished
 * could not express the delta it exists to express.
 *
 * READ OFF THE DRAFT AND NOTHING ELSE, which is what lets the PAGE hold this
 * check while the panel holds the readback: the floor needs the service's
 * answer, an empty input does not.
 */
export function draftExampleProblems(
  parts: readonly DraftPart[],
): { language: string; index: number; message: string }[] {
  const problems: { language: string; index: number; message: string }[] = [];
  for (const language of exampleLanguages(parts)) {
    const document = readExampleDocument(findExamplesPart(parts, language));
    if (document === null) {
      continue;
    }
    document.items.forEach((pair, position) => {
      if (pair.input.length > 0) {
        return;
      }
      problems.push({
        language,
        index: position + 1,
        message: `Example ${position + 1} has no input. The pinned summary agent refuses an empty example input, so this pair cannot be saved until it has one. An empty expected output is fine — that is a valid example.`,
      });
    });
  }
  return problems;
}
